/**
 * Engagement Tracking Service
 * 
 * PURPOSE: Track page views and visit metrics for the Engagement Analytics Console.
 * 
 * PRIVACY PRINCIPLES:
 * - Anonymous session ID (not tied to user identity pre-auth)
 * - No PII stored
 * - No keystroke or click tracking
 * - Route-level granularity only
 * 
 * TRACKED EVENTS:
 * - page_view: Route changes
 * - visit_start: Session begins
 * - visit_end: Session ends (via beforeunload)
 */

// Get backend URL from environment
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Session management
const SESSION_KEY = 'na_engagement_session';
const VISIT_START_KEY = 'na_visit_start';

/**
 * Generate a random anonymous session ID
 */
const generateSessionId = () => {
  return 'anon_' + Math.random().toString(36).substring(2, 15) + 
         Math.random().toString(36).substring(2, 15) + 
         '_' + Date.now().toString(36);
};

/**
 * Get or create anonymous session ID
 */
export const getSessionId = () => {
  let sessionId = sessionStorage.getItem(SESSION_KEY);
  if (!sessionId) {
    sessionId = generateSessionId();
    sessionStorage.setItem(SESSION_KEY, sessionId);
  }
  return sessionId;
};

/**
 * Get anonymous ID from localStorage (persists across sessions)
 */
export const getAnonymousId = () => {
  const ANON_KEY = 'na_anonymous_id';
  let anonId = localStorage.getItem(ANON_KEY);
  if (!anonId) {
    anonId = 'visitor_' + Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
    localStorage.setItem(ANON_KEY, anonId);
  }
  return anonId;
};

/**
 * Detect device type
 */
const getDeviceType = () => {
  const ua = navigator.userAgent;
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return 'tablet';
  }
  if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
    return 'mobile';
  }
  return 'desktop';
};

/**
 * Check if user is authenticated
 * Returns true if there's a valid access token in localStorage
 */
const isUserAuthenticated = () => {
  const token = localStorage.getItem('accessToken');
  return !!token;
};

/**
 * Log an engagement event to the backend
 * 
 * IMPORTANT: Only tracks events for ANONYMOUS/UNAUTHENTICATED users.
 * Authenticated users are already converted - we have separate analytics for them.
 */
export const logEngagementEvent = async (eventType, eventCategory, metadata = {}) => {
  try {
    // FRONTEND GATE: Don't track page views for authenticated users
    // Site Visitors analytics is for understanding guest/unregistered traffic only
    if (isUserAuthenticated()) {
      console.debug('[Engagement] Skipping event for authenticated user:', eventType);
      return;
    }
    
    const payload = {
      event_type: eventType,
      event_category: eventCategory,
      session_id: getSessionId(),
      anonymous_id: getAnonymousId(),
      route: window.location.pathname,
      referrer: document.referrer || null,
      source: 'web',
      metadata: {
        ...metadata,
        device_type: getDeviceType(),
        screen_width: window.innerWidth,
        screen_height: window.innerHeight,
        user_agent: navigator.userAgent.substring(0, 200) // Truncate for storage
      }
    };

    // Use sendBeacon for reliability (especially on page unload)
    if (navigator.sendBeacon && eventType === 'visit_end') {
      const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
      navigator.sendBeacon(`${API_URL}/api/admin/engagement/log`, blob);
    } else {
      // Standard fetch for other events
      await fetch(`${API_URL}/api/admin/engagement/log`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true // Allows request to outlive the page
      });
    }
  } catch (error) {
    // Silently fail - don't disrupt user experience for analytics
    console.debug('[Engagement] Failed to log event:', eventType, error.message);
  }
};

/**
 * Track a page view
 */
export const trackPageView = (route, options = {}) => {
  const { title, referrer } = options;
  
  logEngagementEvent('page_view', 'web_visit', {
    page_title: title || document.title,
    previous_route: referrer || sessionStorage.getItem('na_previous_route'),
    timestamp: new Date().toISOString()
  });

  // Store current route as previous for next navigation
  sessionStorage.setItem('na_previous_route', route);
};

/**
 * Track visit start (call once on app load)
 */
export const trackVisitStart = () => {
  const visitStart = Date.now();
  sessionStorage.setItem(VISIT_START_KEY, visitStart.toString());
  
  logEngagementEvent('visit_start', 'web_visit', {
    entry_page: window.location.pathname,
    referrer: document.referrer || 'direct',
    timestamp: new Date().toISOString()
  });
};

/**
 * Track visit end (call on page unload)
 */
export const trackVisitEnd = () => {
  const visitStart = parseInt(sessionStorage.getItem(VISIT_START_KEY) || '0');
  const durationSeconds = visitStart ? Math.round((Date.now() - visitStart) / 1000) : 0;
  
  logEngagementEvent('visit_end', 'web_visit', {
    exit_page: window.location.pathname,
    duration_seconds: durationSeconds,
    timestamp: new Date().toISOString()
  });
};

/**
 * Initialize engagement tracking
 * Call this once in your app's root component
 */
export const initEngagementTracking = () => {
  // Track visit start
  trackVisitStart();
  
  // Track initial page view
  trackPageView(window.location.pathname);
  
  // Track visit end on page unload
  window.addEventListener('beforeunload', trackVisitEnd);
  
  // Also track on visibility change (mobile browsers)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      trackVisitEnd();
    }
  });

  // Return cleanup function
  return () => {
    window.removeEventListener('beforeunload', trackVisitEnd);
  };
};

/**
 * Hook for tracking route changes in React Router
 * Usage: useEngagementTracking() in a component wrapped by Router
 */
export const createRouteChangeHandler = (location) => {
  trackPageView(location.pathname, {
    title: document.title
  });
};

export default {
  initEngagementTracking,
  trackPageView,
  trackVisitStart,
  trackVisitEnd,
  logEngagementEvent,
  getSessionId,
  getAnonymousId
};
